import { Alert, Cell, Grid, Heading, HFlow, VFlow } from 'bold-ui'
import { useAlert } from 'components/alert'
import CheckPermission from 'components/auth/CheckPermission'
import { Box } from 'components/Box'
import { Form, FormRenderProps, SwitchField } from 'components/form'
import { useAlternarConexaoInternetMutation } from 'graphql/hooks.generated'
import { AlternarConexaoInternetMutationVariables, ConexaoQuery } from 'graphql/types.generated'
import Permissions from 'types/Permissions.generated'
import { metaPath } from 'util/metaPath'

import { ConfiguracaoAgendaOnlineBox } from './agenda-online/ConfiguracaoAgendaOnlineBox'
import { AssinaturaDigitalSection } from './assinatura-digital/AssinaturaDigitalSection'
import { ConfiguracoesCadSusBox } from './cadsus/ConfiguracoesCadSusBox'
import { ConfiguracaoInternetTesteConexao } from './ConfiguracaoInternetTesteConexao'
import { ConfiguracoesHorusBox } from './horus/ConfiguracoesHorusBox'
import { ConfiguracoesVideochamadaBox } from './videochamada/ConfiguracoesVideochamadaBox'

export interface ConfiguracoesInternetBoxProps {
  internet: ConexaoQuery['conexao']['internet']
  cadsus: ConexaoQuery['conexao']['cadsus']
  horus: ConexaoQuery['conexao']['horus']
  videochamada: ConexaoQuery['conexao']['videochamadas']
  agendaOnline: ConexaoQuery['conexao']['agendaOnline']
  assinaturaDigital: ConexaoQuery['conexao']['assinaturaDigital']
  smtpConfigurado: boolean
  refetch(): void
}

const meta = metaPath<AlternarConexaoInternetMutationVariables>()

export function ConfiguracoesInternetBox(props: Readonly<ConfiguracoesInternetBoxProps>) {
  const { internet, cadsus, horus, videochamada, agendaOnline, assinaturaDigital, smtpConfigurado, refetch } = props
  const internetHabilitada = internet.habilitado
  const alert = useAlert()

  const [alternarConexaoMutation] = useAlternarConexaoInternetMutation()

  const renderForm = (formProps: FormRenderProps<AlternarConexaoInternetMutationVariables>) => {
    const label: string = internetHabilitada ? 'Habilitado' : 'Desabilitado'

    return (
      <VFlow>
        <HFlow justifyContent='space-between'>
          <Heading style={{ display: 'inline' }} level={3}>
            Internet
          </Heading>
          <SwitchField label={label} name={meta.habilitado} onChange={handleChange(formProps)} />
        </HFlow>
        <Alert type='info' inline>
          Ao desabilitar a conexão com a internet as funcionalidades de Agenda Online, conexão com CADSUS, Envio Online,
          Hórus, Assinatura digital, RNDS, Unificação de bases e Videochamadas serão desabilitadas.
        </Alert>
        {formProps.values.habilitado && <ConfiguracaoInternetTesteConexao refetch={refetch} internet={internet} />}
      </VFlow>
    )
  }

  const handleChange = (formProps: FormRenderProps) => () => {
    formProps.handleSubmit()
  }

  const handleSubmit = (values: AlternarConexaoInternetMutationVariables) => {
    return alternarConexaoMutation({ variables: values }).then(() => {
      const state: string = values.habilitado ? 'habilitada' : 'desabilitada'
      alert('success', 'Conexão com internet ' + state + ' com sucesso.')
      refetch()
    })
  }

  return (
    <Box>
      {internet && (
        <VFlow vSpacing={0.5}>
          <Form<AlternarConexaoInternetMutationVariables>
            onSubmit={handleSubmit}
            initialValues={{ habilitado: internetHabilitada }}
            render={renderForm}
          />
          <Grid gap={2}>
            <Cell size={6}>
              <ConfiguracoesCadSusBox internetHabilitada={internetHabilitada} cadsus={cadsus} load={refetch} />
            </Cell>
            <Cell size={6}>
              <ConfiguracoesHorusBox internetHabilitada={internetHabilitada} horus={horus} load={refetch} />
            </Cell>
            <CheckPermission permission={Permissions.configuracaoDeInstalacao.configurarVideochamadas}>
              <Cell size={6}>
                <ConfiguracoesVideochamadaBox
                  internetHabilitada={internetHabilitada}
                  videochamada={videochamada}
                  load={refetch}
                />
              </Cell>
              <Cell size={6} />
            </CheckPermission>
            <Cell size={12}>
              <ConfiguracaoAgendaOnlineBox
                agendaOnline={agendaOnline}
                internetHabilitada={internetHabilitada}
                refetch={refetch}
              />
            </Cell>
            {assinaturaDigital && (
              <CheckPermission permission={Permissions.configuracaoDeInstalacao.configurarPrescricaoDigital}>
                <Cell size={12}>
                  <AssinaturaDigitalSection
                    assinaturaDigital={assinaturaDigital}
                    internetHabilitada={internetHabilitada}
                    smtpConfigurado={smtpConfigurado}
                    refetch={refetch}
                  />
                </Cell>
              </CheckPermission>
            )}
          </Grid>
        </VFlow>
      )}
    </Box>
  )
}
